// =============================================================================
// sharedFacilityHelpers.js - Helper functions for facility sharing
// =============================================================================
// Functions to check conflicts and validate day access when clubs share facilities
// =============================================================================

import { db } from '../utils/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

/**
 * Get shared facility status for a club
 * @param {string} clubId - The club ID
 * @returns {Object|null} - The shared facility status or null
 */
export const getSharedFacilityStatus = async (clubId) => {
  try {
    const statusRef = doc(db, 'clubs', clubId, 'sharedFacility', 'status');
    const statusDoc = await getDoc(statusRef);
    
    if (statusDoc.exists() && statusDoc.data().isSharing) {
      return statusDoc.data();
    }
    
    return null;
  } catch (error) {
    console.error('Error getting shared facility status:', error);
    return null;
  }
};

/**
 * Check if a club can book on a specific day based on day access rules
 * @param {Object} sharedStatus - The shared facility status
 * @param {string} dayOfWeek - Day of week (lowercase: 'monday', 'tuesday', etc)
 * @param {boolean} isHost - Whether this club is the host (true) or guest (false)
 * @returns {boolean} - True if club can book on this day
 */
export const canBookOnDay = (sharedStatus, dayOfWeek, isHost) => {
  // If not sharing, can always book
  if (!sharedStatus || !sharedStatus.isSharing) {
    return true;
  }

  // Get day access rules
  const dayAccessRules = sharedStatus.dayAccessRules || {};
  const dayRule = dayAccessRules[dayOfWeek.toLowerCase()] || 'both';

  console.log(`🗓️ Day access check: ${dayOfWeek}, rule: ${dayRule}, isHost: ${isHost}, role: ${sharedStatus.role}`);

  // Check access based on rule
  if (dayRule === 'both') {
    return true; // Both clubs can book
  } else if (dayRule === 'my_club') {
    // "my_club" always means the HOST club (the club that initiated/owns the facility)
    return isHost;
  } else if (dayRule === 'their_club') {
    // "their_club" always means the GUEST club (the club that accepted the invitation)
    return !isHost;
  }

  return true; // Default to allowing if rule is unclear
};

/**
 * Get day of week from date string
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Day of week (lowercase: 'monday', 'tuesday', etc)
 */
export const getDayOfWeek = (dateString) => {
  const date = new Date(dateString);
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
};

/**
 * Check for allocation conflicts including partner club's allocations
 * @param {string} clubId - Current club ID
 * @param {string} pitchId - Pitch ID (e.g., 'pitch-1')
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} startTime - Start time (e.g., '17:00')
 * @param {string} endTime - End time (e.g., '17:30')
 * @param {Array} sections - Array of sections being booked (e.g., ['A', 'B'])
 * @param {string|null} excludeAllocationId - Allocation ID to exclude (for edits)
 * @returns {Object} - { hasConflict: boolean, conflictDetails: Array }
 */
export const checkSharedFacilityConflicts = async (
  clubId,
  pitchId,
  date,
  startTime,
  endTime,
  sections = [],
  excludeAllocationId = null
) => {
  try {
    console.log('🔍 Checking conflicts for shared facility...');
    console.log('Parameters:', { clubId, pitchId, date, startTime, endTime, sections });

    // Get shared facility status
    const sharedStatus = await getSharedFacilityStatus(clubId);
    
    // Array to store all clubs we need to check
    const clubsToCheck = [clubId];
    
    if (sharedStatus && sharedStatus.isSharing && sharedStatus.partnerClubId) {
      clubsToCheck.push(sharedStatus.partnerClubId);
      console.log('🤝 Facility is shared, checking partner club too:', sharedStatus.partnerClubId);
    }

    const conflicts = [];

    // Check allocations for each club
    for (const checkClubId of clubsToCheck) {
      console.log(`📋 Checking allocations for club: ${checkClubId}`);
      
      // Query allocations for this pitch and date
      // Path: clubs/{clubId}/allocations/{pitchId}/items
      const allocationsRef = collection(db, 'clubs', checkClubId, 'allocations', pitchId, 'items');
      const allocationsQuery = query(
        allocationsRef,
        where('date', '==', date)
      );
      
      const querySnapshot = await getDocs(allocationsQuery);
      
      console.log(`Found ${querySnapshot.size} allocations for ${checkClubId} on ${date}`);

      querySnapshot.forEach((doc) => {
        const allocation = doc.data();
        
        // Skip if this is the allocation we're editing
        if (excludeAllocationId && doc.id === excludeAllocationId) {
          return;
        }

        // Check time overlap
        const hasTimeOverlap = checkTimeOverlap(
          startTime,
          endTime,
          allocation.startTime,
          allocation.endTime
        );

        if (!hasTimeOverlap) {
          return;
        }

        // Check section overlap
        const allocationSections = allocation.sections || [allocation.section];
        const hasSectionOverlap = checkSectionOverlap(sections, allocationSections);

        if (hasSectionOverlap) {
          conflicts.push({
            allocationId: doc.id,
            clubId: checkClubId,
            team: allocation.team || allocation.teamName,
            startTime: allocation.startTime,
            endTime: allocation.endTime,
            sections: allocationSections,
            type: allocation.type || allocation.allocationType
          });
        }
      });
    }

    console.log(`Found ${conflicts.length} conflicts`);
    
    return {
      hasConflict: conflicts.length > 0,
      conflictDetails: conflicts
    };

  } catch (error) {
    console.error('Error checking shared facility conflicts:', error);
    return {
      hasConflict: false,
      conflictDetails: [],
      error: error.message
    };
  }
};

/**
 * Check if two time ranges overlap
 * @param {string} start1 - Start time 1 (HH:MM)
 * @param {string} end1 - End time 1 (HH:MM)
 * @param {string} start2 - Start time 2 (HH:MM)
 * @param {string} end2 - End time 2 (HH:MM)
 * @returns {boolean} - True if times overlap
 */
const checkTimeOverlap = (start1, end1, start2, end2) => {
  const toMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const start1Min = toMinutes(start1);
  const end1Min = toMinutes(end1);
  const start2Min = toMinutes(start2);
  const end2Min = toMinutes(end2);

  // Check if ranges overlap
  return start1Min < end2Min && end1Min > start2Min;
};

/**
 * Check if two section arrays overlap
 * @param {Array} sections1 - Array of sections (e.g., ['A', 'B'])
 * @param {Array} sections2 - Array of sections (e.g., ['B', 'C'])
 * @returns {boolean} - True if sections overlap
 */
const checkSectionOverlap = (sections1, sections2) => {
  // If either allocation doesn't specify sections, assume full pitch (conflict)
  if (!sections1 || sections1.length === 0 || !sections2 || sections2.length === 0) {
    return true;
  }

  // Check if any section exists in both arrays
  return sections1.some(section => sections2.includes(section));
};

/**
 * Get user-friendly conflict message
 * @param {Array} conflicts - Array of conflict details
 * @param {string} currentClubId - The current club's ID (optional, for partner detection)
 * @returns {string} - Formatted conflict message
 */
export const getConflictMessage = (conflicts, currentClubId = null) => {
  if (!conflicts || conflicts.length === 0) {
    return '';
  }

  // Get unique sections that have conflicts
  const conflictingSections = [...new Set(conflicts.flatMap(c => c.sections || []))].sort();
  const sectionText = conflictingSections.length > 0 
    ? `Section${conflictingSections.length > 1 ? 's' : ''} ${conflictingSections.join(', ')}`
    : 'The selected time slot';

  // Check if any conflicts are from partner club
  const hasPartnerConflicts = currentClubId && conflicts.some(c => c.clubId !== currentClubId);
  const partnerNote = hasPartnerConflicts ? '\n\nNote: Some conflicts are from your partner club.' : '';

  return `${sectionText} ${conflictingSections.length > 1 ? 'are' : 'is'} already booked during this time.${partnerNote}`;
};

/**
 * Validate day access before creating allocation
 * @param {string} clubId - Current club ID
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Object} - { canBook: boolean, message: string }
 */
export const validateDayAccess = async (clubId, dateString) => {
  try {
    const sharedStatus = await getSharedFacilityStatus(clubId);
    
    // If not sharing, can always book
    if (!sharedStatus || !sharedStatus.isSharing) {
      return { canBook: true, message: '' };
    }

    const dayOfWeek = getDayOfWeek(dateString);
    // Check if this club is the host (initiator/facility owner) or guest (invited)
    const isHost = sharedStatus.role === 'initiator' || sharedStatus.role === 'host';
    
    const canBook = canBookOnDay(sharedStatus, dayOfWeek, isHost);
    
    if (!canBook) {
      const partnerName = sharedStatus.partnerClubName || 'partner club';
      const dayName = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      
      return {
        canBook: false,
        message: `${dayName}s are reserved for ${isHost ? partnerName : 'the host club'} according to your day access schedule.`
      };
    }

    return { canBook: true, message: '' };

  } catch (error) {
    console.error('Error validating day access:', error);
    // On error, allow booking (fail open)
    return { canBook: true, message: '' };
  }
};

/**
 * Load allocations from partner club for display (greyed out)
 * @param {string} clubId - Current club ID
 * @param {string} pitchId - Pitch ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Object} - { partnerAllocations: Array, partnerClubName: string }
 */
export const loadPartnerAllocations = async (clubId, pitchId, date) => {
  try {
    const sharedStatus = await getSharedFacilityStatus(clubId);
    
    if (!sharedStatus || !sharedStatus.isSharing || !sharedStatus.partnerClubId) {
      return { partnerAllocations: [], partnerClubName: null };
    }

    const partnerClubId = sharedStatus.partnerClubId;
    const partnerClubName = sharedStatus.partnerClubName;

    console.log(`📥 Loading partner allocations from ${partnerClubName} (${partnerClubId})`);

    // Load partner's allocations for this pitch and date
    // Path: clubs/{clubId}/allocations/{pitchId}/items
    const allocationsRef = collection(db, 'clubs', partnerClubId, 'allocations', pitchId, 'items');
    const allocationsQuery = query(
      allocationsRef,
      where('date', '==', date)
    );

    const querySnapshot = await getDocs(allocationsQuery);
    const partnerAllocations = [];

    querySnapshot.forEach((doc) => {
      partnerAllocations.push({
        id: doc.id,
        ...doc.data(),
        isPartnerAllocation: true, // Mark as partner allocation
        partnerClubName: partnerClubName
      });
    });

    console.log(`✅ Loaded ${partnerAllocations.length} partner allocations`);

    return {
      partnerAllocations,
      partnerClubName
    };

  } catch (error) {
    console.error('Error loading partner allocations:', error);
    return { partnerAllocations: [], partnerClubName: null };
  }
};
